import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card } from '@mui/material';

import { EmptyBox } from '@europrocurement/flexy-components';
import { additionalFieldIcon, descriptionIcon } from '@europrocurement/l2d-icons';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useOffreService } from '@europrocurement/l2d-domain';
import { ChampComplMentaireJsonldChampcomplementaireRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';

import Form from './Form';
import { additionalFieldFichePath, additionalFieldListPath } from './routes';
import FicheHeader, { type FicheSubtitleElement } from '../components/fiche/FicheHeader';
import buildCreationTitle from '../components/fiche/buildCreationTitle';
import LoadingScreen from '../forms/LoadingScreen';

const ADDITIONAL_FIELD_SECTION_LABEL = 'Configuration des offres commerciales';

/**
 * Fiche d'un champ complémentaire sur route dédiée (remplace l'édition/création en modale).
 * - `admin/offres/champ-complementaire/new` : création.
 * - `admin/offres/champ-complementaire/:id` : édition (chargé par son identifiant).
 */
const AdditionalFieldFiche: React.FunctionComponent = function () {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isCreate = !id || id === 'new';

    const { additionalFieldModel: model } = useOffreService();
    const { request } = useApiRequest<ChampComplMentaireJsonldChampcomplementaireRead>();

    const [entity, setEntity] = useState<
        ChampComplMentaireJsonldChampcomplementaireRead | undefined
    >(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(!isCreate);
    const [isInError, setIsInError] = useState<boolean>(false);
    // Libellé saisi dans le formulaire (mode création), affiché dans l'en-tête.
    const [draftLabel, setDraftLabel] = useState<string>('');

    useEffect(() => {
        let active = true;

        if (!isCreate) {
            setIsLoading(true);
            setIsInError(false);

            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => {
                    if (active) {
                        setEntity(result.data);
                    }
                },
                failureCallback: () => {
                    if (active) {
                        setIsInError(true);
                    }
                },
            }).finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        }

        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isCreate]);

    // Après un enregistrement :
    // - en création : on ouvre la fiche du nouvel objet ;
    // - en édition : on reste sur la fiche et on rafraîchit l'entité
    //   (sans toggler le chargement, pour ne pas démonter le formulaire).
    const handleAfterSubmit = useCallback(
        (createdId?: number) => {
            if (isCreate) {
                if (createdId != null) {
                    navigate(additionalFieldFichePath(createdId));
                }
                return;
            }
            if (!id) {
                return;
            }
            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => setEntity(result.data),
            });
        },
        [id, isCreate, model, navigate, request],
    );

    const title = isCreate
        ? buildCreationTitle("Création d'un champ complémentaire", draftLabel)
        : (entity?.libelle ?? 'Champ complémentaire');

    const subtitleElements: FicheSubtitleElement[] =
        isCreate || !entity
            ? []
            : [
                  {
                      icon: additionalFieldIcon.props.icon,
                      label: entity.htmlFieldType
                          ? `Type de champ : ${entity.htmlFieldType}`
                          : undefined,
                  },
                  {
                      icon: descriptionIcon.props.icon,
                      label: entity.description ? `Description : ${entity.description}` : undefined,
                  },
              ];

    const renderContent = () => {
        if (isLoading) {
            return <LoadingScreen />;
        }

        if (isInError || (!isCreate && !entity)) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '30vh',
                    }}
                >
                    <EmptyBox
                        text="Champ complémentaire introuvable"
                        subText="Ce champ complémentaire n'existe pas ou n'a pas pu être chargé."
                    />
                </Box>
            );
        }

        return (
            <Form
                isCreate={isCreate}
                entity={entity}
                afterSubmit={handleAfterSubmit}
                onLabelChange={setDraftLabel}
            />
        );
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <FicheHeader
                icon={additionalFieldIcon}
                title={title}
                reference={isCreate ? undefined : entity?.id}
                subtitleElements={subtitleElements}
                sectionLabel={ADDITIONAL_FIELD_SECTION_LABEL}
                listPath={additionalFieldListPath}
            />

            <Card sx={{ marginTop: 2, padding: 3 }}>{renderContent()}</Card>
        </Box>
    );
};

export default AdditionalFieldFiche;
